(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SWIPE_THRESHOLD = 50;
  const POST_SWIPE_CLICK_DELAY = 500;

  class ChakrikkaHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.refreshRefs();
      this.isTransitioning = false;
      this.autoplayTimer = null;
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.pointerId = null;
      this.suppressClickUntil = 0;
      this.transitionToken = (this.transitionToken || 0) + 1;
      this.paused = false;

      this._onPrev = () => this.go(-1);
      this._onNext = () => this.go(1);
      this._onPointerDown = this.onPointerDown.bind(this);
      this._onPointerUp = this.onPointerUp.bind(this);
      this._onPointerCancel = this.onPointerCancel.bind(this);
      this._onClickCapture = this.onClickCapture.bind(this);
      this._onEnter = () => this.pauseAutoplay();
      this._onLeave = () => this.resumeAutoplay();
      this._onBlockSelect = this.onBlockSelect.bind(this);
      this._onSectionLoad = this.onSectionLoad.bind(this);

      this.bindControls();
      this.syncSlideAccessibility();

      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusin', this._onEnter);
      this.addEventListener('focusout', this._onLeave);

      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:section:load', this._onSectionLoad);

      this.setupIntersectionObserver();

      if (this.autoplayEnabled && this.slides.length > 1) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.transitionToken += 1;
      this.unbindControls();
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      this.removeEventListener('focusin', this._onEnter);
      this.removeEventListener('focusout', this._onLeave);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      this.stopAutoplay();
      if (this._io) this._io.disconnect();
    }

    refreshRefs() {
      this.slides = Array.from(this.querySelectorAll('.chakrikka-hero__slide'));
      this.viewport = this.querySelector('.chakrikka-hero__viewport');
      this.prevBtn = this.querySelector('.chakrikka-hero__nav-btn--prev');
      this.nextBtn = this.querySelector('.chakrikka-hero__nav-btn--next');
      this.announcer = this.querySelector('[data-chakrikka-hero-announcer]');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this.transitionMs = parseInt(this.dataset.transitionMs, 10) || 800;
    }

    bindControls() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      if (!this.viewport) return;

      this.viewport.addEventListener('click', this._onClickCapture, true);

      if (this.slides.length > 1) {
        this.viewport.addEventListener('pointerdown', this._onPointerDown, { passive: true });
        this.viewport.addEventListener('pointerup', this._onPointerUp, { passive: true });
        this.viewport.addEventListener('pointercancel', this._onPointerCancel, { passive: true });
      }
    }

    unbindControls() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);

      if (!this.viewport) return;

      this.viewport.removeEventListener('click', this._onClickCapture, true);
      this.viewport.removeEventListener('pointerdown', this._onPointerDown);
      this.viewport.removeEventListener('pointerup', this._onPointerUp);
      this.viewport.removeEventListener('pointercancel', this._onPointerCancel);
    }

    get activeIndex() {
      const i = this.slides.findIndex(
        (s) => s.classList.contains('is-active') && !s.classList.contains('is-leaving')
      );
      return i >= 0 ? i : 0;
    }

    syncSlideAccessibility(activeSlide = this.slides[this.activeIndex]) {
      this.slides.forEach((slide) => {
        const isActive = slide === activeSlide;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', !isActive);
      });
    }

    setupIntersectionObserver() {
      if (this._io) this._io.disconnect();
      this._offscreen = false;
      if (!('IntersectionObserver' in window) || !this.viewport) return;
      this._io = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((e) => e.isIntersecting);
          this._offscreen = !visible;
          if (visible) this.resumeAutoplay();
          else this.pauseAutoplay();
        },
        { root: null, threshold: 0.15 }
      );
      this._io.observe(this.viewport);
    }

    announce(index) {
      if (!this.announcer) return;
      const n = index + 1;
      const total = this.slides.length;
      this.announcer.textContent = `Slide ${n} of ${total}`;
    }

    go(delta) {
      if (this.slides.length < 2 || this.isTransitioning) return;
      const len = this.slides.length;
      const current = this.slides[this.activeIndex];
      let nextIndex = this.activeIndex + delta;
      if (nextIndex < 0) nextIndex = len - 1;
      if (nextIndex >= len) nextIndex = 0;
      const next = this.slides[nextIndex];
      if (!current || !next || current === next) return;

      this.isTransitioning = true;
      this.pauseAutoplay();
      const transitionToken = ++this.transitionToken;

      const reduced = REDUCED_MOTION.matches;

      if (reduced) {
        current.classList.remove('is-active', 'is-leaving', 'is-entering');
        next.classList.remove('is-leaving', 'is-entering');
        next.classList.add('is-active');
        this.syncSlideAccessibility(next);
        this.isTransitioning = false;
        this.announce(nextIndex);
        this.resumeAutoplay();
        return;
      }

      next.classList.add('is-entering');
      void next.offsetWidth;

      requestAnimationFrame(() => {
        if (transitionToken !== this.transitionToken) return;

        requestAnimationFrame(() => {
          if (transitionToken !== this.transitionToken) return;

          current.classList.add('is-leaving');
          next.classList.add('is-active');
          this.syncSlideAccessibility(next);

          let finished = false;

          const finishTransition = () => {
            if (finished || transitionToken !== this.transitionToken) return;
            finished = true;
            current.classList.remove('is-active', 'is-leaving');
            next.classList.remove('is-entering');
            next.classList.add('is-active');
            this.syncSlideAccessibility(next);
            this.isTransitioning = false;
            this.announce(nextIndex);
            this.resumeAutoplay();
          };

          const onEnd = (e) => {
            if (e.target !== next || e.propertyName !== 'opacity') return;
            next.removeEventListener('transitionend', onEnd);
            finishTransition();
          };

          next.addEventListener('transitionend', onEnd);

          window.setTimeout(() => {
            if (finished) return;
            next.removeEventListener('transitionend', onEnd);
            finishTransition();
          }, this.transitionMs + 80);
        });
      });
    }

    onPointerDown(e) {
      if (e.isPrimary === false) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.pointerStartX = e.clientX;
      this.pointerStartY = e.clientY;
      this.pointerId = e.pointerId;
    }

    onPointerUp(e) {
      if (this.pointerStartX == null || e.pointerId !== this.pointerId) return;
      const dx = e.clientX - this.pointerStartX;
      const dy = e.clientY - this.pointerStartY;
      this.resetPointer();

      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

      this.suppressClickUntil = window.performance.now() + POST_SWIPE_CLICK_DELAY;
      if (dx < 0) this.go(1);
      else this.go(-1);
    }

    onPointerCancel() {
      this.resetPointer();
    }

    resetPointer() {
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.pointerId = null;
    }

    onClickCapture(event) {
      const now = window.performance.now();

      if (now < this.suppressClickUntil) {
        this.suppressClickUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      this.suppressClickUntil = 0;

      const target = event.target instanceof Element ? event.target : null;
      const slideLink = target && target.closest('[data-slide-link]');
      const isDesignMode = this.dataset.designMode === 'true';

      if (isDesignMode && slideLink && this.contains(slideLink)) {
        event.preventDefault();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || this.slides.length < 2 || this._offscreen) return;
      this.autoplayTimer = window.setInterval(() => {
        if (!this.paused && !this.isTransitioning) this.go(1);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplay() {
      this.paused = true;
      this.stopAutoplay();
    }

    resumeAutoplay() {
      this.paused = false;
      if (this._offscreen) return;
      this.startAutoplay();
    }

    onBlockSelect(event) {
      const sid = event.detail && event.detail.sectionId;
      if (!sid || String(sid) !== String(this.dataset.sectionId)) return;
      const blockId = event.detail.blockId;
      if (!blockId) return;
      const slide = this.querySelector(`[data-block-id="${blockId}"]`);
      if (!slide) return;
      const idx = this.slides.indexOf(slide);
      if (idx < 0) return;

      this.pauseAutoplay();
      this.transitionToken += 1;
      this.slides.forEach((s) => s.classList.remove('is-active', 'is-leaving', 'is-entering'));
      slide.classList.add('is-active');
      this.syncSlideAccessibility(slide);
      this.isTransitioning = false;
      this.announce(idx);
      this.resumeAutoplay();
    }

    onSectionLoad(event) {
      const sid = event.detail && event.detail.sectionId;
      if (!sid || String(sid) !== String(this.dataset.sectionId)) return;
      this.transitionToken += 1;
      this.isTransitioning = false;
      this.unbindControls();
      this.refreshRefs();
      this.resetPointer();
      this.suppressClickUntil = 0;
      this.bindControls();
      this.syncSlideAccessibility();
      this.stopAutoplay();
      this.setupIntersectionObserver();
      if (this.autoplayEnabled && this.slides.length > 1) this.startAutoplay();
    }
  }

  if (!customElements.get('chakrikka-hero-slideshow')) {
    customElements.define('chakrikka-hero-slideshow', ChakrikkaHeroSlideshow);
  }
})();
